import { formatBalance } from "./dashboardHelpers";
import { formatAppDate } from "./appTimeFormat";
import {
  getAcceptedCapitalPartners,
  isPartneredHouse,
} from "./betHousePartners";

export const OPERATOR_MANAGE_HOME = "/app/bethouse/manage";

/** Pick the house for this URL, falling back to the newest operator house. */
export const resolveOperatorHouse = (houses = [], houseId) => {
  if (!houses.length) return null;
  if (houseId) {
    const match = houses.find((h) => String(h._id) === String(houseId));
    if (match) return match;
  }
  return houses[0];
};

/** Keep showing the last dashboard snapshot while a refresh or house switch is in flight. */
export const operatorDashboardForHouse = ({
  dashboard,
  dashboardHouseId,
  dashboardsByHouse,
  houseId,
  dashboardStatus,
  dashboardStatusByHouse = {},
}) => {
  const cached =
    houseId && dashboardsByHouse?.[String(houseId)]
      ? dashboardsByHouse[String(houseId)]
      : null;
  const resolvedDashboard =
    cached ||
    (dashboard && String(dashboardHouseId) === String(houseId) ? dashboard : null);
  const resolvedStatus =
    dashboardStatusByHouse[String(houseId)] ||
    (String(dashboardHouseId) === String(houseId) ? dashboardStatus : "idle");

  if (!resolvedDashboard || !houseId) {
    return {
      dashboard: null,
      isStale: false,
      isRefreshing: resolvedStatus === "loading",
    };
  }

  const matches =
    Boolean(cached) || String(dashboardHouseId) === String(houseId);
  if (matches) {
    return {
      dashboard: resolvedDashboard,
      isStale: false,
      isRefreshing: resolvedStatus === "loading",
    };
  }

  if (resolvedStatus === "loading") {
    return { dashboard: resolvedDashboard, isStale: true, isRefreshing: true };
  }

  return { dashboard: null, isStale: false, isRefreshing: false };
};

export const operatorHouseNeedsDetail = (house) => Boolean(house?._operatorSummary);

export const operatorManagePath = (houses = [], houseId) => {
  const house = resolveOperatorHouse(houses, houseId);
  return house ? `${OPERATOR_MANAGE_HOME}/${house._id}` : OPERATOR_MANAGE_HOME;
};

export const OPERATOR_SECTIONS = [
  { id: "pulse", label: "Pulse", hint: "Live ops & controls" },
  { id: "treasury", label: "Treasury", hint: "Balance & ledger" },
  { id: "loan", label: "Loan", hint: "House credit line" },
  { id: "partners", label: "Partners", hint: "Co-owners & invites" },
  { id: "matching", label: "Matching", hint: "Cross-house liquidity" },
  { id: "risk", label: "Risk", hint: "Exposure & fixtures" },
  { id: "fixtures", label: "Board", hint: "Fixture lanes" },
  { id: "bettors", label: "Bettors", hint: "Top customers" },
  { id: "tickets", label: "Tickets", hint: "Open positions" },
  { id: "viewers", label: "Viewers", hint: "Profile views" },
  { id: "followers", label: "Followers", hint: "Follow events" },
  { id: "activity", label: "Activity", hint: "Settlements & tape" },
];

export const OPERATOR_NAV_CATEGORIES = [
  { id: "overview", label: "Overview", sectionIds: ["pulse"] },
  { id: "finance", label: "Finance", sectionIds: ["treasury", "loan"] },
  { id: "house", label: "House", sectionIds: ["partners", "matching", "risk"] },
  { id: "board", label: "Board", sectionIds: ["fixtures"] },
  {
    id: "audience",
    label: "Audience",
    sectionIds: ["bettors", "tickets", "viewers", "followers"],
  },
  { id: "tape", label: "Tape", sectionIds: ["activity"] },
];

const operatorSectionMap = Object.fromEntries(
  OPERATOR_SECTIONS.map((section) => [section.id, section])
);

export const getOperatorSection = (sectionId) => operatorSectionMap[sectionId] || null;

export const OPERATOR_DESK_SECTIONS = new Set([
  "fixtures",
  "bettors",
  "tickets",
  "viewers",
  "followers",
]);

export const operatorDeskPath = (houseId, sectionId) => {
  if (!houseId || !OPERATOR_DESK_SECTIONS.has(sectionId)) return null;
  if (sectionId === "fixtures") {
    return `/app/bethouse/manage/${houseId}/fixtures`;
  }
  return `/app/bethouse/manage/${houseId}/${sectionId}`;
};

export const operatorAddFixturesPath = (houseId) =>
  houseId ? `/app/bethouse/manage/${houseId}/fixtures/add` : null;

export const HOUSE_STATUS = {
  active: { label: "Active", tone: "success" },
  paused: { label: "Paused", tone: "warning" },
  draft: { label: "Draft", tone: "primary" },
  closed: { label: "Closed", tone: "danger" },
};

export const formatCash = (value, opts) => formatBalance(value, opts);

export const houseMonogram = (name = "") => {
  const trimmed = String(name).trim();
  if (!trimmed) return "BH";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
};

export const formatFounded = (value) => {
  if (!value) return "—";
  return formatAppDate(value);
};

export const partnerCount = (bet) => getAcceptedCapitalPartners(bet).length;

export { isPartneredHouse };

export const LEDGER_TONE = {
  DEPOSIT: { sign: "+", tone: "credit" },
  STAKE_RECEIVED: { sign: "+", tone: "credit" },
  LIABILITY_RELEASED: { sign: "+", tone: "credit" },
  WITHDRAWAL: { sign: "-", tone: "debit" },
  LIABILITY_LOCKED: { sign: "-", tone: "debit" },
  PAYOUT_MADE: { sign: "-", tone: "debit" },
  PLATFORM_COMMISSION: { sign: "-", tone: "debit" },
};

export const SETTLEMENT_TONE = {
  won: { tone: "debit", label: "Bettor won" },
  paid: { tone: "debit", label: "Bettor paid" },
  lost: { tone: "credit", label: "Bettor lost" },
  voided: { tone: "muted", label: "Voided" },
  refunded: { tone: "muted", label: "Refunded" },
};

/** House-side cash impact for a settled ticket (matches mobile operator console). */
export const settlementHouseDisplay = (row = {}) => {
  const meta = SETTLEMENT_TONE[row.outcome] || {
    tone: "muted",
    label: row.outcome || "Settled",
  };
  const outcome = String(row.outcome || "").toLowerCase();
  if (outcome === "lost") {
    return { ...meta, sign: "+", amount: Number(row.stake) || 0 };
  }
  if (outcome === "won" || outcome === "paid") {
    return { ...meta, sign: "-", amount: Number(row.payout) || 0 };
  }
  return { ...meta, sign: "", amount: Number(row.stake) || 0 };
};

export const ledgerLabel = (type = "") =>
  String(type || "MOVEMENT")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const shouldSyncDashboard = (status) =>
  ["active", "paused"].includes(String(status || "").toLowerCase());

export const normalizeGoLiveCheck = (check = {}) => ({
  id: check.id || check.key || "check",
  title: check.title || check.label || check.key || check.id || "Check",
  ok: Boolean(check.ok ?? check.pass),
  detail: check.detail || null,
  severity: check.severity || "block",
});

/** Failed blockers first — most useful in the pulse panel summary. */
export const sortReadinessChecksForDisplay = (checks = [], limit = 6) => {
  const normalized = checks.map(normalizeGoLiveCheck);
  const failed = normalized.filter((c) => !c.ok);
  const passed = normalized.filter((c) => c.ok);
  return [...failed, ...passed].slice(0, limit);
};

export const pulseStatValue = (value, { isDraft, hasDashboard, draftFallback = 0 } = {}) => {
  if (value != null && value !== "") return value;
  if (isDraft && !hasDashboard) return draftFallback;
  return "—";
};
